import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FeatherCheck, FeatherX } from '../ui/icons';
import LeaveApplicationList from './LeaveApplicationList';
import './PendingApprovals.css';

const PendingApprovals = ({ pendingLeaves, onApprove, onReject, loading, currentUserId }) => {
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(null);

  // Separate own leaves from others' leaves
  const ownPendingLeaves = pendingLeaves.filter(leave => leave.user_id === currentUserId);
  const othersPendingLeaves = pendingLeaves.filter(leave => leave.user_id !== currentUserId);

  const handleReject = async (leaveId) => {
    if (!rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setRejectingId(leaveId);
    try {
      await onReject(leaveId, rejectionReason);
      setShowRejectForm(null);
      setRejectionReason('');
    } catch (error) {
      console.error('Error rejecting leave:', error);
    } finally {
      setRejectingId(null);
    }
  };

  if (!pendingLeaves || pendingLeaves.length === 0) {
    return (
      <div className="pending-approvals">
        <div className="pending-approvals-header">
          <h3>Pending Approvals (0)</h3>
        </div>
        <div className="pending-approvals-empty">
          <p>No pending leave applications</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-approvals">
      <div className="pending-approvals-header">
        <h3>Pending Approvals ({othersPendingLeaves.length})</h3>
      </div>
      
      {/* Show manager's own pending leaves separately */}
      {ownPendingLeaves.length > 0 && (
        <div className="own-pending-leaves-section">
          <h4 className="own-leaves-title">Your Pending Leave Applications</h4>
          <div className="pending-approvals-list">
            {ownPendingLeaves.map((leave) => (
              <div key={leave.id} className="pending-approval-card own-leave">
                <div className="pending-approval-content">
                  <div className="pending-approval-info">
                    <div className="pending-approval-employee">
                      <strong>Your Leave Application</strong>
                    </div>
                    <div className="pending-approval-dates">
                      {new Date(leave.start_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })} - {new Date(leave.end_date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                    <div className="pending-approval-details">
                      <span>{leave.total_days} {leave.total_days === 1 ? 'day' : 'days'}</span>
                      {leave.leave_type && <span> • {leave.leave_type}</span>}
                    </div>
                    {leave.reason && (
                      <div className="pending-approval-reason">
                        <strong>Reason:</strong> {leave.reason}
                      </div>
                    )}
                  </div>
                  <Badge variant="warning">Pending Approval</Badge>
                </div>
                <div className="pending-approval-note">
                  <p>Waiting for another manager to approve your leave application.</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Others' pending leaves that can be approved */}
      <div className="pending-approvals-list">
        {othersPendingLeaves.length === 0 ? (
          <div className="pending-approvals-empty">
            <p>No pending leave applications to approve</p>
          </div>
        ) : (
          othersPendingLeaves.map((leave) => (
          <div key={leave.id} className="pending-approval-card">
            <div className="pending-approval-content">
              <div className="pending-approval-info">
                <div className="pending-approval-employee">
                  <strong>{leave.user?.display_name || leave.user?.email || 'Employee'}</strong>
                </div>
                <div className="pending-approval-dates">
                  {new Date(leave.start_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })} - {new Date(leave.end_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </div>
                <div className="pending-approval-details">
                  <span>{leave.total_days} {leave.total_days === 1 ? 'day' : 'days'}</span>
                  {leave.leave_type && <span> • {leave.leave_type}</span>}
                </div>
                {leave.reason && (
                  <div className="pending-approval-reason">
                    <strong>Reason:</strong> {leave.reason}
                  </div>
                )}
              </div>
              <Badge variant="warning">Pending</Badge>
            </div>

            {showRejectForm === leave.id ? (
              <div className="pending-approval-reject-form">
                <textarea
                  className="rejection-reason-input"
                  placeholder="Enter rejection reason..."
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows="3"
                />
                <div className="reject-form-actions">
                  <Button
                    variant="neutral-tertiary"
                    onClick={() => {
                      setShowRejectForm(null);
                      setRejectionReason('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="neutral-tertiary"
                    onClick={() => handleReject(leave.id)}
                    disabled={rejectingId === leave.id || !rejectionReason.trim()}
                    style={{ color: '#dc2626', borderColor: '#dc2626' }}
                  >
                    {rejectingId === leave.id ? 'Rejecting...' : 'Confirm Reject'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="pending-approval-actions">
                <Button
                  variant="primary"
                  icon={<FeatherCheck />}
                  onClick={() => onApprove(leave.id)}
                  disabled={loading}
                  style={{ backgroundColor: '#059669' }}
                >
                  Approve
                </Button>
                <Button
                  variant="neutral-tertiary"
                  icon={<FeatherX />}
                  onClick={() => setShowRejectForm(leave.id)}
                  disabled={loading}
                  style={{ color: '#dc2626', borderColor: '#dc2626' }}
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
          ))
        )}
      </div>
    </div>
  );
};

export default PendingApprovals;

